import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { feedbackAPI, authAPI, socialAPI } from '../services/api';
import { Eye, EyeOff, Scale, Lock, FileText } from 'lucide-react';

const Settings = () => {
  const { user, updateProfile } = useAuth();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [teamName, setTeamName] = useState(user?.team_name || '');

  // Initialize names from user display_name or fallback to empty


  const defaultPrefs = user?.profile?.notification_preferences || {};
  const [notificationPrefs, setNotificationPrefs] = useState(defaultPrefs);
  const [language, setLanguage] = useState(i18n.language);
  const [isPrivate, setIsPrivate] = useState(user?.profile?.is_private || false);

  const handlePrivacyToggle = async (checked) => {
    setIsPrivate(checked);
    try {
      await socialAPI.updateProfile({ is_private: checked });
      updateProfile({ profile: { ...user.profile, is_private: checked } });
      toast({ title: t('settings.privacyUpdated', 'Gizlilik ayarları güncellendi') });
    } catch (error) {
      console.error('Failed to update privacy preference', error);
      toast({ title: t('common.error', 'Bir hata oluştu'), variant: "destructive" });
      setIsPrivate(!checked);
    }
  };





  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
    toast({ title: t('settings.language') + ' changed to ' + (newLang === 'en' ? 'English' : 'Türkçe') });
  };

  const handleNotificationToggle = async (key, checked) => {
    const newPrefs = { ...notificationPrefs, [key]: checked };
    setNotificationPrefs(newPrefs);

    try {
      await socialAPI.updateProfile({ notification_preferences: newPrefs });
      updateProfile({ profile: { ...user.profile, notification_preferences: newPrefs } });
      toast({ title: t('settings.notificationsUpdated') });
    } catch (error) {
      console.error('Failed to update notification preferences', error);
      toast({ title: t('common.error', 'Bir hata oluştu'), variant: "destructive" });
      setNotificationPrefs(notificationPrefs); // Revert on fail
    }
  };

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">{t('settings.title')}</h1>
        <p className="text-gray-400">{t('settings.subtitle')}</p>
      </div>

      <Tabs defaultValue="settings" className="w-full">
        <TabsList className="bg-[#1a1a1b] border-gray-800 mb-6">
          <TabsTrigger value="settings">{t('nav.settings')}</TabsTrigger>
          {(user?.role === 'player' || user?.role === 'standard') && user?.membership_type !== 'seller' && (
            <TabsTrigger value="upgrade">{t('upgrade.title')}</TabsTrigger>
          )}
          <TabsTrigger value="feedback">{t('common.feedback') || 'Geri Bildirim'}</TabsTrigger>
          <TabsTrigger value="about">{t('common.about') || 'Hakkında'}</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="space-y-6">
          {/* Profile Section */}


          {/* Preferences Section */}
          <Card className="bg-[#1a1a1b] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">{t('settings.preferences')}</CardTitle>
              <CardDescription className="text-gray-400">{t('settings.customizeExperience')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="language" className="text-white">{t('settings.language')}</Label>
                <Select value={language} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="tr">Türkçe</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-4 border-t border-gray-800 space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-white mb-1">{t('settings.privacy', 'Gizlilik')}</h3>
                  <p className="text-sm text-gray-400 mb-4">{t('settings.privacyDesc', 'Profil etkileşimlerinizi ve görünürlüğünüzü yönetin.')}</p>
                </div>
                <div className="bg-[#0a0a0b] rounded-lg border border-gray-800 p-4">
                  <div className="flex items-center justify-between">
                    <div className="mr-8">
                      <Label htmlFor="privacy_toggle" className="text-gray-300 font-bold block mb-1">Gizli Profil</Label>
                      <span className="text-xs text-gray-500">
                        Gizli profillerin paylaşımları Keşfet bölümünde çıkmaz. Profilinizi ziyaret eden yabancılar sadece Öne Çıkarılan içeriklerinizi görebilir ve standart paylaşımlarınıza erişemezler.
                      </span>
                    </div>
                    <Switch
                      id="privacy_toggle"
                      checked={isPrivate}
                      onCheckedChange={handlePrivacyToggle}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-800 space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-white mb-1">{t('settings.notifications', 'Bildirimler')}</h3>
                  <p className="text-sm text-gray-400 mb-4">{t('settings.notificationsDesc', 'Hangi bildirimleri almak istediğinizi seçin.')}</p>
                </div>

                <div className="space-y-6">
                  {/* Sosyal (Social) */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wider">{t('nav.social')}</h4>
                    <div className="bg-[#0a0a0b] rounded-lg border border-gray-800 p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="notif_post_like" className="text-gray-300 font-normal">Gönderi Beğenileri</Label>
                        <Switch
                          id="notif_post_like"
                          checked={notificationPrefs.POST_LIKE !== false}
                          onCheckedChange={(c) => handleNotificationToggle('POST_LIKE', c)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="notif_post_comment" className="text-gray-300 font-normal">Gönderi Yorumları</Label>
                        <Switch
                          id="notif_post_comment"
                          checked={notificationPrefs.POST_COMMENT !== false}
                          onCheckedChange={(c) => handleNotificationToggle('POST_COMMENT', c)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="notif_new_follower" className="text-gray-300 font-normal">Yeni Takipçiler</Label>
                        <Switch
                          id="notif_new_follower"
                          checked={notificationPrefs.NEW_FOLLOWER !== false}
                          onCheckedChange={(c) => handleNotificationToggle('NEW_FOLLOWER', c)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Takım (Team) */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wider">{t('settings.team')}</h4>
                    <div className="bg-[#0a0a0b] rounded-lg border border-gray-800 p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="notif_team_invite" className="text-gray-300 font-normal">Takım Davetleri</Label>
                        <Switch
                          id="notif_team_invite"
                          checked={notificationPrefs.TEAM_INVITE !== false}
                          onCheckedChange={(c) => handleNotificationToggle('TEAM_INVITE', c)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="notif_team_announcement" className="text-gray-300 font-normal">Takım Duyuruları</Label>
                        <Switch
                          id="notif_team_announcement"
                          checked={notificationPrefs.TEAM_ANNOUNCEMENT !== false}
                          onCheckedChange={(c) => handleNotificationToggle('TEAM_ANNOUNCEMENT', c)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="notif_training_update" className="text-gray-300 font-normal">İdman Saati/Yeri Değişiklikleri</Label>
                        <Switch
                          id="notif_training_update"
                          checked={notificationPrefs.TRAINING_UPDATE !== false}
                          onCheckedChange={(c) => handleNotificationToggle('TRAINING_UPDATE', c)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="notif_new_training" className="text-gray-300 font-normal">Yeni Antrenman Seansı</Label>
                        <Switch
                          id="notif_new_training"
                          checked={notificationPrefs.NEW_TRAINING !== false}
                          onCheckedChange={(c) => handleNotificationToggle('NEW_TRAINING', c)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="notif_new_playbook" className="text-gray-300 font-normal">Yeni Oyun Kitabı Eklendi</Label>
                        <Switch
                          id="notif_new_playbook"
                          checked={notificationPrefs.NEW_PLAYBOOK !== false}
                          onCheckedChange={(c) => handleNotificationToggle('NEW_PLAYBOOK', c)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Market (Marketplace) */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-amber-400 uppercase tracking-wider">{t('nav.market')}</h4>
                    <div className="bg-[#0a0a0b] rounded-lg border border-gray-800 p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="notif_market_message" className="text-gray-300 font-normal">Pazaryeri Mesajları</Label>
                        <Switch
                          id="notif_market_message"
                          checked={notificationPrefs.MARKET_MESSAGE !== false}
                          onCheckedChange={(c) => handleNotificationToggle('MARKET_MESSAGE', c)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="notif_market_status" className="text-gray-300 font-normal">İlan Durumu Güncellemeleri</Label>
                        <Switch
                          id="notif_market_status"
                          checked={notificationPrefs.MARKET_STATUS !== false}
                          onCheckedChange={(c) => handleNotificationToggle('MARKET_STATUS', c)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Security Section */}
          <Card className="bg-[#1a1a1b] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">{t('settings.securitySettings')}</CardTitle>
              <CardDescription className="text-gray-400">{t('settings.managePassword')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ChangePasswordDialog />
            </CardContent>
          </Card>

          {/* Save Button */}

        </TabsContent>

        {(user?.role === 'player' || user?.role === 'standard') && user?.membership_type !== 'seller' && (
          <TabsContent value="upgrade">
            <Card className="bg-[#1a1a1b] border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">{t('upgrade.title')}</CardTitle>
                <CardDescription className="text-gray-400">
                  {t('upgrade.subtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <CoachUpgradeForm />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="feedback">
          <FeedbackTab />
        </TabsContent>

        <TabsContent value="about">
          <AboutTab />
        </TabsContent>
      </Tabs>


    </div >
  );
};

function FeedbackTab() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [type, setType] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast({ title: t('feedback.required'), variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      await feedbackAPI.submit({ type, message });
      toast({ title: t('feedback.success') });
      setMessage('');
    } catch (error) {
      console.error(error);
      toast({ title: t('feedback.error'), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="bg-[#1a1a1b] border-gray-800">
      <CardHeader>
        <CardTitle className="text-white">{t('feedback.title')}</CardTitle>
        <CardDescription className="text-gray-400">{t('feedback.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-white">{t('feedback.type')}</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="suggestion">{t('feedback.suggestion')}</SelectItem>
              <SelectItem value="complaint">{t('feedback.complaint')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="message" className="text-white">{t('feedback.message')}</Label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full h-32 bg-[#0a0a0b] border border-gray-700 rounded-md p-2 text-white resize-none focus:outline-none focus:border-blue-500"
            placeholder={t('feedback.placeholder')}
          />
        </div>
        <Button onClick={handleSubmit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white">
          {submitting ? t('feedback.submitting') : t('feedback.submit')}
        </Button>
      </CardContent>
    </Card>
  );
}

function AboutTab() {
  const { t } = useTranslation();
  return (
    <Card className="bg-[#1a1a1b] border-gray-800">
      <CardHeader>
        <CardTitle className="text-white">{t('about.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 text-gray-300">
        <div className="space-y-4">
          <p>{t('about.p1')}</p>
          <p>{t('about.p2')}</p>
          <p>{t('about.p3')}</p>
          <p>{t('about.p4')}</p>
          <p>{t('about.p5')}</p>
        </div>

        <div className="pt-6 border-t border-gray-800">
          <h4 className="text-white font-medium mb-3">Yasal Metinler</h4>
          <div className="flex flex-col gap-2">
            <Link to="/legal?type=KVKK" className="text-blue-500 hover:underline text-sm flex items-center gap-2">
              <Scale className="w-4 h-4" /> 6698 Sayılı KVKK Aydınlatma Metni
            </Link>
            <Link to="/legal?type=PRIVACY" className="text-blue-500 hover:underline text-sm flex items-center gap-2">
              <Lock className="w-4 h-4" /> Gizlilik Politikası
            </Link>
            <Link to="/legal?type=TERMS" className="text-blue-500 hover:underline text-sm flex items-center gap-2">
              <FileText className="w-4 h-4" /> Kullanım Şartları ve Üyelik Sözleşmesi
            </Link>
          </div>
        </div>

        <div className="pt-6 border-t border-gray-800 text-sm text-gray-500">
          <p>{t('about.version')}: v0.9 (Beta)</p>
          <p>{t('about.contact')}: info@usesnapp.net</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ChangePasswordDialog() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Request, 2: Verify
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset state when dialog opens/closes
  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
    if (!newOpen) {
      setTimeout(() => {
        setStep(1);
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
      }, 300);
    }
  };

  const handleSendCode = async () => {
    setLoading(true);
    try {
      await authAPI.requestPasswordOtp();
      toast({ title: t('auth.otpSent', 'Verification code sent to your email') });
      setStep(2);
    } catch (error) {
      toast({
        title: error.response?.data?.detail || 'Failed to send verification code',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (!otp || !newPassword || !confirmPassword) {
      toast({ title: t('auth.fillAllFields', 'Please fill all fields'), variant: "destructive" });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({ title: t('auth.passwordsDoNotMatch', 'Passwords do not match'), variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      await authAPI.verifyPasswordOtp({
        otp,
        new_password: newPassword
      });

      toast({ title: t('settings.successUpdate', 'Password updated successfully') });
      handleOpenChange(false);
    } catch (error) {
      const msg = error.response?.data?.detail || t('settings.errorUpdate', 'Failed to update password');
      toast({ title: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white w-full">
          {t('settings.changePassword', 'Change Password')}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('settings.updatePassword', 'Update Password')}</DialogTitle>
          <DialogDescription className="text-gray-400">
            {step === 1
              ? "To secure your account, we'll send a verification code to your email."
              : "Enter the code sent to your email and your new password."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="py-6 space-y-4">
            <Button
              onClick={handleSendCode}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? t('common.sending', 'Sending...') : "Send Verification Code"}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleUpdatePassword} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="otp">Verification Code</Label>
              <Input
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter 6-digit code"
                className="bg-[#0a0a0b] border-gray-700 text-white tracking-widest text-center text-lg"
                maxLength={6}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password">{t('settings.newPassword', 'New Password')}</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-[#0a0a0b] border-gray-700 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">{t('settings.confirmPassword', 'Confirm Password')}</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-[#0a0a0b] border-gray-700 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Back
              </Button>
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white flex-1">
                {loading ? t('common.updating', 'Updating...') : t('settings.updatePassword', 'Update Password')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CoachUpgradeForm() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [teamCode, setTeamCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleUpgrade = async (e) => {
    e.preventDefault();
    if (!teamCode.trim()) return;

    setSubmitting(true);
    try {
      await authAPI.upgradeToCoach(teamCode);
      toast({ title: t('upgrade.success'), description: t('upgrade.successDesc') });
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      let errorMessage = "Invalid invitation code";
      try {
        const detail = error.response?.data?.detail;

        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => String(err?.msg || 'Validation error')).join(', ');
        } else if (detail && typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      } catch (e) {
        console.error("Error parsing error message", e);
      }

      toast({
        title: t('upgrade.failed'),
        description: String(errorMessage),
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleUpgrade} className="flex gap-4 items-end">
        <div className="flex-1 space-y-2">
          <Label htmlFor="teamCode" className="text-white">{t('upgrade.label')}</Label>
          <Input
            id="teamCode"
            value={teamCode}
            onChange={(e) => setTeamCode(e.target.value)}
            placeholder={t('upgrade.placeholder')}
            className="bg-[#0a0a0b] border-gray-700 text-white"
            maxLength={8}
          />
        </div>
        <Button type="submit" disabled={submitting || !teamCode} className="bg-green-600 hover:bg-green-700 text-white">
          {submitting ? t('upgrade.verifying') : t('upgrade.button')}
        </Button>
      </form>

      <div className="pt-6 border-t border-gray-800">
        <h4 className="text-lg font-semibold text-white mb-2">{t('upgrade.noTeamTitle')}</h4>
        <p className="text-gray-400 mb-4 text-sm">
          {t('upgrade.noTeamDesc')}
        </p>
        <p className="text-gray-400 text-sm">
          {t('upgrade.contactUs')} <a href="mailto:info@usesnapp.net" className="text-blue-500 hover:text-blue-400">info@usesnapp.net</a>
        </p>
      </div>
    </div>
  );
}

export default Settings;
